/**
 * Per-trip TCS / GST government-deposit tracking (Task 2 — Part 1).
 *
 * Shown on the trip's Financials tab. Reads `GET /api/tax/trip/{tripId}` for the
 * tax collected (from this trip's non-cancelled invoices), the filing period,
 * the quarter/month return status, and any saved deposit record. Lets the agent
 * record the government deposit (paid? date / mode / bank-charges / challan /
 * remarks) via `POST /api/tax/deposit`, which also syncs the Tax Calendar.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Landmark, CheckCircle2, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEPOSIT_MODES = ['NEFT', 'RTGS', 'Challan', 'Cash', 'Other'];
const fmtINR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const TaxDepositCard = ({ tripId }) => {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogTax, setDialogTax] = useState(null); // 'tcs' | 'gst'
  const [form, setForm] = useState({ paymentDate: todayISO(), mode: 'NEFT', bankCharges: '0', challanNumber: '', remarks: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!tripId) { setLoading(false); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tax/trip/${encodeURIComponent(tripId)}`, { withCredentials: true });
      setTracking(res.data);
    } catch (err) {
      // Trip may have no invoices yet — keep the card silent rather than noisy.
      setTracking(null);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { load(); }, [load]);

  const openDialog = (tax) => {
    const dep = (tracking && tracking[tax] && tracking[tax].deposit) || {};
    setForm({
      paymentDate: dep.paymentDate || todayISO(),
      mode: dep.mode || 'NEFT',
      bankCharges: String(dep.bankCharges ?? '0'),
      challanNumber: dep.challanNumber || '',
      remarks: dep.remarks || '',
    });
    setDialogTax(tax);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/tax/deposit`, {
        entityType: 'trip',
        entityId: tracking?.tripId || tripId,
        tax: dialogTax,
        paidToGovt: true,
        paymentDate: form.paymentDate,
        mode: form.mode,
        bankCharges: Number(form.bankCharges) || 0,
        challanNumber: form.challanNumber,
        remarks: form.remarks,
      }, { withCredentials: true });
      toast.success(`${dialogTax.toUpperCase()} deposit recorded · Tax Calendar updated`);
      setDialogTax(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record deposit');
    } finally {
      setSaving(false);
    }
  };

  const revert = async (tax) => {
    if (!window.confirm('Mark this as NOT yet deposited?')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/tax/deposit`, {
        entityType: 'trip', entityId: tracking?.tripId || tripId, tax,
        paidToGovt: false, paymentDate: '', mode: '', bankCharges: 0, challanNumber: '', remarks: '',
      }, { withCredentials: true });
      toast.success('Reverted to pending');
      load();
    } catch (err) {
      toast.error('Failed to revert');
    }
  };

  if (loading) return null;
  const tcs = tracking?.tcs;
  const gst = tracking?.gst;
  const hasAny = (tcs && tcs.collected > 0) || (gst && gst.collected > 0);

  return (
    <Card className="overflow-hidden" data-testid="tax-deposit-card">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Landmark className="h-4 w-4 text-slate-400" />
          Tax Compliance — Government Deposit
        </h3>
      </div>
      {!hasAny ? (
        <div className="text-center py-6 text-xs text-slate-400">
          No TCS / GST collected on this trip yet (issue a tax invoice first).
        </div>
      ) : (
        <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <TaxBlock tax="tcs" data={tcs} onMark={() => openDialog('tcs')} onRevert={() => revert('tcs')} />
          <TaxBlock tax="gst" data={gst} onMark={() => openDialog('gst')} onRevert={() => revert('gst')} />
        </div>
      )}

      <Dialog open={!!dialogTax} onOpenChange={(o) => { if (!o) setDialogTax(null); }}>
        <DialogContent className="max-w-md" data-testid="trip-tax-deposit-dialog">
          <DialogHeader>
            <DialogTitle>Confirm {(dialogTax || '').toUpperCase()} deposit to government</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Records this trip's {(dialogTax || '').toUpperCase()} deposit and updates the Tax Calendar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payment date</Label>
                <Input type="date" value={form.paymentDate} onChange={(e) => setForm((f) => ({ ...f, paymentDate: e.target.value }))} className="mt-1" data-testid="trip-tax-date" />
              </div>
              <div>
                <Label className="text-xs">Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
                  <SelectTrigger className="mt-1" data-testid="trip-tax-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DEPOSIT_MODES.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Bank charges ₹</Label>
                <Input type="number" min="0" value={form.bankCharges} onChange={(e) => setForm((f) => ({ ...f, bankCharges: e.target.value }))} className="mt-1" data-testid="trip-tax-charges" />
              </div>
              <div>
                <Label className="text-xs">Challan number</Label>
                <Input value={form.challanNumber} onChange={(e) => setForm((f) => ({ ...f, challanNumber: e.target.value }))} className="mt-1" data-testid="trip-tax-challan" placeholder="CIN / BSR ref" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Remarks</Label>
              <Input value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} className="mt-1" data-testid="trip-tax-remarks" placeholder="Optional note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogTax(null)} data-testid="trip-tax-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="trip-tax-confirm">
              {saving ? 'Saving…' : 'Confirm deposit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const TaxBlock = ({ tax, data, onMark, onRevert }) => {
  const label = tax.toUpperCase();
  const collected = Number(data?.collected || 0);
  const dep = data?.deposit || {};
  const paid = !!dep.paidToGovt;
  if (collected <= 0) {
    return (
      <div className="rounded-lg border border-slate-100 p-3" data-testid={`trip-tax-block-${tax}`}>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-xs text-slate-400 mt-1">No {label} on this trip.</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`trip-tax-block-${tax}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label} collected</div>
        <div className="font-mono font-semibold text-slate-800 text-sm">{fmtINR(collected)}</div>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">{data?.periodLabel || '—'}</div>
      <div className="mt-2 flex items-center gap-2">
        {data?.returnFiled ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`trip-tax-return-${tax}`}>
            <CheckCircle2 className="h-3 w-3" /> Return Filed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`trip-tax-return-${tax}`}>
            <Clock className="h-3 w-3" /> Return Pending
          </span>
        )}
      </div>
      <div className="mt-3 border-t border-slate-100 pt-2">
        {paid ? (
          <div className="space-y-1" data-testid={`trip-tax-deposited-${tax}`}>
            <div className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> Deposited{dep.paymentDate ? ` on ${dep.paymentDate}` : ''}
            </div>
            <div className="text-[11px] text-slate-500">
              {[dep.mode, dep.challanNumber ? `Challan ${dep.challanNumber}` : '', Number(dep.bankCharges) ? `Bank chg ${fmtINR(dep.bankCharges)}` : ''].filter(Boolean).join(' · ') || '—'}
            </div>
            {dep.remarks ? <div className="text-[11px] text-slate-400 italic">{dep.remarks}</div> : null}
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onMark} data-testid={`trip-tax-edit-${tax}`}>Edit</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-500" onClick={onRevert} data-testid={`trip-tax-undo-${tax}`}>Undo</Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-xs text-amber-600 font-medium">Not yet deposited</span>
            <Button size="sm" className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800 text-white" onClick={onMark} data-testid={`trip-tax-mark-${tax}`}>
              Mark Deposited
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxDepositCard;
